/**
 * Financial-statement engine — the detailed (data-room) side of a deal's numbers.
 *
 * `deals.financials` holds three statements (P&L, balance sheet, cash flow) as line items with
 * one value per fiscal-year column, normalised to ₹ crore. Everything an analyst reads on the
 * Financials tab is DERIVED here:
 *
 *   finSeries()        canonical line lookup + the safe accounting derivations (EBIT from
 *                      EBITDA − depreciation, net worth from capital + reserves, FCF from
 *                      OCF − capex, …). Only identities that cannot be wrong are applied.
 *   computeRatios()    the full ratio pack (growth, profitability, returns, liquidity,
 *                      leverage, efficiency, cash flow) — one value per year, computed, never
 *                      extracted, so every ratio ties back to statement lines on screen.
 *   statementsFromDeal() a thin fallback built from the deck-level headline figures + `pnl`,
 *                      so the tab reads properly before the data-room file lands.
 *
 * All functions are PURE and total: a missing input yields `null` (rendered "—"), never a throw
 * and never a fabricated number.
 */
import type {
  Deal,
  FinLine,
  FinLineKey,
  FinStatement,
  FinancialStatements,
  RatioAnalysis,
  RatioGroup,
  RatioRow,
  RatioSummary,
} from './types.js';

/** Every canonical line key, grouped by the statement it belongs to (also drives extraction). */
export const PNL_KEYS = [
  'revenue', 'otherIncome', 'totalIncome', 'cogs', 'grossProfit', 'employeeCost', 'otherExpenses',
  'totalExpenses', 'ebitda', 'depreciation', 'ebit', 'interest', 'pbt', 'exceptional', 'tax', 'pat', 'eps',
] as const;
export const BS_KEYS = [
  'shareCapital', 'reserves', 'netWorth', 'longTermDebt', 'shortTermDebt', 'totalDebt',
  'otherNonCurrentLiabilities', 'totalLiabilities', 'netFixedAssets', 'cwip', 'investments',
  'otherNonCurrentAssets', 'inventory', 'receivables', 'cash', 'otherCurrentAssets', 'currentAssets',
  'payables', 'otherCurrentLiabilities', 'currentLiabilities', 'totalAssets',
] as const;
export const CF_KEYS = [
  'ocf', 'capex', 'icf', 'fincf', 'fcf', 'interestPaid', 'taxPaid', 'dividend', 'netCashFlow',
] as const;

/** All canonical keys — used to validate an extraction before it is persisted. */
export const FIN_LINE_KEYS: ReadonlySet<string> = new Set<string>([...PNL_KEYS, ...BS_KEYS, ...CF_KEYS]);

export const isFinLineKey = (k: unknown): k is FinLineKey => typeof k === 'string' && FIN_LINE_KEYS.has(k);

type Series = (number | null)[];

const fin = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/* ───────────────────────────── year labels ───────────────────────────── */

/**
 * Sort key for a fiscal-year column label — "FY22" → 22, "FY2024" → 24, "FY27 (E)" → 27.
 * Unparseable labels sort last (0 would put them first, which reads as a data bug).
 */
export function fyNum(label: string): number {
  const m = /(\d{2,4})/.exec(String(label ?? ''));
  if (!m) return 9999;
  const n = Number(m[1]);
  return n >= 1900 ? n - 2000 : n;
}

/** True for a projection/estimate column ("FY27E", "FY27 (E)", "FY28 Proj") — excluded from actuals. */
export function isProjectedYear(label: string): boolean {
  const s = String(label ?? '').trim();
  return /\(?\s*[EPF]\s*\)?$/i.test(s.replace(/\s+/g, '')) || /proj|est|forecast|budget|plan|guid/i.test(s);
}

/** Index of the newest ACTUAL (non-projection) year, or the last column if every year is a projection. */
export function latestActualIdx(years: string[]): number {
  for (let i = years.length - 1; i >= 0; i--) if (!isProjectedYear(years[i]!)) return i;
  return years.length - 1;
}

/* ───────────────────────────── statement helpers ───────────────────────────── */

const EMPTY_STATEMENT: FinStatement = { lines: [] };

/** True when a statement has at least one line carrying at least one figure. */
export function statementHasContent(st: FinStatement | null | undefined): boolean {
  return !!st?.lines?.some((l) => (l.values ?? []).some((v) => fin(v) != null));
}

/** True when the statements carry anything at all worth rendering. */
export function finHasContent(fs: FinancialStatements | null | undefined): boolean {
  if (!fs || !Array.isArray(fs.years) || !fs.years.length) return false;
  return statementHasContent(fs.pnl) || statementHasContent(fs.balanceSheet) || statementHasContent(fs.cashFlow);
}

/** The value of a canonical line for one year, straight from the statements (no derivation). */
export function finLineValue(fs: FinancialStatements, key: FinLineKey, yearIdx: number): number | null {
  for (const st of [fs.pnl, fs.balanceSheet, fs.cashFlow]) {
    const l = st?.lines?.find((x) => x.key === key);
    if (l) return fin(l.values?.[yearIdx]);
  }
  return null;
}

/**
 * Canonical series per key, with the safe accounting identities filled in.
 *
 * Only derivations that are definitionally true are applied (EBITDA = EBIT + depreciation,
 * net worth = share capital + reserves, FCF = OCF − capex, total assets = total liabilities …).
 * Anything that depends on a convention the document might not follow (e.g. whether short-term
 * debt sits inside current liabilities) is left alone — the ratio simply shows "—" instead of a
 * number the analyst can't reconcile.
 */
export function finSeries(fs: FinancialStatements): Record<string, Series> {
  const n = fs.years?.length ?? 0;
  const s: Record<string, Series> = {};
  for (const st of [fs.pnl, fs.balanceSheet, fs.cashFlow]) {
    for (const l of st?.lines ?? []) {
      if (!l.key || !isFinLineKey(l.key) || s[l.key]) continue; // first occurrence of a key wins
      s[l.key] = Array.from({ length: n }, (_, i) => fin(l.values?.[i]));
    }
  }
  const v = (k: string, i: number): number | null => s[k]?.[i] ?? null;
  const sum = (i: number, ...ks: string[]): number | null => {
    let t = 0;
    for (const k of ks) { const x = v(k, i); if (x == null) return null; t += x; }
    return t;
  };
  // Fill only the null cells of `k`, in dependency order. Each candidate is tried in turn.
  const fill = (k: string, ...cands: ((i: number) => number | null)[]) => {
    const out: Series = Array.from({ length: n }, (_, i) => {
      const own = v(k, i);
      if (own != null) return own;
      for (const c of cands) { const x = c(i); if (x != null) return x; }
      return null;
    });
    s[k] = out;
  };

  fill('netWorth', (i) => sum(i, 'shareCapital', 'reserves'));
  fill('totalDebt', (i) => sum(i, 'longTermDebt', 'shortTermDebt'));
  fill('grossProfit', (i) => { const r = v('revenue', i), c = v('cogs', i); return r != null && c != null ? r - Math.abs(c) : null; });
  fill('pbt', (i) => { const p = v('pat', i), t = v('tax', i); return p != null && t != null ? p + Math.abs(t) : null; });
  fill('pat', (i) => { const p = v('pbt', i), t = v('tax', i); return p != null && t != null ? p - Math.abs(t) : null; });
  fill('ebit',
    (i) => { const e = v('ebitda', i), d = v('depreciation', i); return e != null && d != null ? e - Math.abs(d) : null; },
    (i) => { const p = v('pbt', i), int = v('interest', i); return p != null && int != null ? p + Math.abs(int) : null; },
  );
  fill('ebitda',
    (i) => { const e = v('ebit', i), d = v('depreciation', i); return e != null && d != null ? e + Math.abs(d) : null; },
    (i) => { const p = v('pbt', i), int = v('interest', i), d = v('depreciation', i); return p != null && int != null && d != null ? p + Math.abs(int) + Math.abs(d) : null; },
  );
  fill('currentAssets', (i) => {
    const parts = sum(i, 'inventory', 'receivables', 'cash');
    if (parts == null) return null;
    return parts + (v('otherCurrentAssets', i) ?? 0);
  });
  fill('totalAssets', (i) => v('totalLiabilities', i));
  fill('totalLiabilities', (i) => v('totalAssets', i));
  fill('fcf', (i) => { const o = v('ocf', i), cx = v('capex', i); return o != null && cx != null ? o - Math.abs(cx) : null; });
  return s;
}

/* ───────────────────────────── ratio analysis ───────────────────────────── */

const div = (a: number | null, b: number | null): number | null =>
  a == null || b == null || Math.abs(b) < 1e-9 ? null : a / b;
const pctOf = (a: number | null, b: number | null): number | null => { const r = div(a, b); return r == null ? null : r * 100; };
const daysOf = (a: number | null, b: number | null): number | null => { const r = div(a, b); return r == null ? null : r * 365; };

/** Compound annual growth from `a` to `b` over `yrs` years, in % (null unless both are positive). */
export function cagr(a: number | null, b: number | null, yrs: number): number | null {
  if (a == null || b == null || a <= 0 || b <= 0 || yrs <= 0) return null;
  return (Math.pow(b / a, 1 / yrs) - 1) * 100;
}

/**
 * The full ratio pack, computed off the statements. One value per year column; a ratio whose
 * inputs the document didn't state comes back null (the UI renders "—").
 *
 * Balance-sheet ratios use CLOSING balances (not averages), so every number can be tied straight
 * back to a line on the balance sheet above it.
 */
export function computeRatios(fs: FinancialStatements): RatioAnalysis {
  const years = fs?.years ?? [];
  const n = years.length;
  if (!n) return { years: [], groups: [], summary: [] };
  const s = finSeries(fs);
  const v = (k: string, i: number) => s[k]?.[i] ?? null;
  const each = (f: (i: number) => number | null): Series => Array.from({ length: n }, (_, i) => f(i));
  const sub = (a: number | null, b: number | null) => (a == null || b == null ? null : a - b);
  const abs = (a: number | null) => (a == null ? null : Math.abs(a));
  const yoy = (k: string) => each((i) => (i === 0 ? null : pctOf(sub(v(k, i), v(k, i - 1)), abs(v(k, i - 1)))));

  const row = (key: string, label: string, unit: RatioRow['unit'], dir: RatioRow['dir'], values: Series, note?: string): RatioRow =>
    ({ key, label, unit, dir, values, ...(note ? { note } : {}) });

  // Effective tax rate feeds RoIC; keep it as one computation.
  const taxRate = each((i) => { const r = div(abs(v('tax', i)), v('pbt', i)); return r == null ? null : Math.min(Math.max(r, 0), 0.6); });

  const groups: RatioGroup[] = [
    {
      key: 'growth', label: 'Growth',
      rows: [
        row('revGrowth', 'Revenue growth', '%', 'up', yoy('revenue'), 'YoY change in revenue'),
        row('ebitdaGrowth', 'EBITDA growth', '%', 'up', yoy('ebitda'), 'YoY change in EBITDA'),
        row('patGrowth', 'PAT growth', '%', 'up', yoy('pat'), 'YoY change in PAT'),
      ],
    },
    {
      key: 'profitability', label: 'Profitability',
      rows: [
        row('grossMargin', 'Gross margin', '%', 'up', each((i) => pctOf(v('grossProfit', i), v('revenue', i))), 'Gross profit ÷ revenue'),
        row('ebitdaMargin', 'EBITDA margin', '%', 'up', each((i) => pctOf(v('ebitda', i), v('revenue', i))), 'EBITDA ÷ revenue'),
        row('ebitMargin', 'EBIT margin', '%', 'up', each((i) => pctOf(v('ebit', i), v('revenue', i))), 'EBIT ÷ revenue'),
        row('pbtMargin', 'PBT margin', '%', 'up', each((i) => pctOf(v('pbt', i), v('revenue', i))), 'PBT ÷ revenue'),
        row('patMargin', 'PAT margin', '%', 'up', each((i) => pctOf(v('pat', i), v('revenue', i))), 'PAT ÷ revenue'),
        row('taxRate', 'Effective tax rate', '%', null, each((i) => { const r = taxRate[i]; return r == null ? null : r * 100; }), 'Tax ÷ PBT'),
      ],
    },
    {
      key: 'returns', label: 'Returns',
      rows: [
        row('roe', 'RoE', '%', 'up', each((i) => pctOf(v('pat', i), v('netWorth', i))), 'PAT ÷ closing net worth'),
        row('roce', 'RoCE', '%', 'up', each((i) => pctOf(v('ebit', i), sum2(v('netWorth', i), v('totalDebt', i)))), 'EBIT ÷ (net worth + total debt)'),
        row('roa', 'RoA', '%', 'up', each((i) => pctOf(v('pat', i), v('totalAssets', i))), 'PAT ÷ total assets'),
        row('roic', 'RoIC', '%', 'up', each((i) => {
          const nopat = v('ebit', i) == null || taxRate[i] == null ? null : v('ebit', i)! * (1 - taxRate[i]!);
          const capital = sum2(v('netWorth', i), v('totalDebt', i));
          const cash = v('cash', i);
          return pctOf(nopat, capital == null ? null : capital - (cash ?? 0));
        }), 'EBIT × (1 − tax rate) ÷ (capital employed − cash)'),
      ],
    },
    {
      key: 'liquidity', label: 'Liquidity',
      rows: [
        row('currentRatio', 'Current ratio', 'x', 'up', each((i) => div(v('currentAssets', i), v('currentLiabilities', i))), 'Current assets ÷ current liabilities'),
        row('quickRatio', 'Quick ratio', 'x', 'up', each((i) => { const ca = v('currentAssets', i), inv = v('inventory', i); return div(ca == null ? null : ca - (inv ?? 0), v('currentLiabilities', i)); }), '(Current assets − inventory) ÷ current liabilities'),
        row('cashRatio', 'Cash ratio', 'x', 'up', each((i) => div(v('cash', i), v('currentLiabilities', i))), 'Cash & equivalents ÷ current liabilities'),
      ],
    },
    {
      key: 'leverage', label: 'Leverage & coverage',
      rows: [
        row('de', 'Debt / equity', 'x', 'down', each((i) => div(v('totalDebt', i), v('netWorth', i))), 'Total debt ÷ net worth'),
        row('netDe', 'Net debt / equity', 'x', 'down', each((i) => { const d = v('totalDebt', i); return div(d == null ? null : d - (v('cash', i) ?? 0), v('netWorth', i)); }), '(Total debt − cash) ÷ net worth'),
        row('debtEbitda', 'Debt / EBITDA', 'x', 'down', each((i) => div(v('totalDebt', i), v('ebitda', i))), 'Total debt ÷ EBITDA'),
        row('netDebtEbitda', 'Net debt / EBITDA', 'x', 'down', each((i) => { const d = v('totalDebt', i); return div(d == null ? null : d - (v('cash', i) ?? 0), v('ebitda', i)); }), '(Total debt − cash) ÷ EBITDA'),
        row('interestCover', 'Interest coverage', 'x', 'up', each((i) => div(v('ebit', i), abs(v('interest', i)))), 'EBIT ÷ interest cost'),
        row('debtorFunding', 'Debt / total assets', 'x', 'down', each((i) => div(v('totalDebt', i), v('totalAssets', i))), 'Total debt ÷ total assets'),
      ],
    },
    {
      key: 'efficiency', label: 'Working capital & efficiency',
      rows: [
        row('receivableDays', 'Receivable days', 'days', 'down', each((i) => daysOf(v('receivables', i), v('revenue', i))), 'Receivables ÷ revenue × 365'),
        row('inventoryDays', 'Inventory days', 'days', 'down', each((i) => daysOf(v('inventory', i), abs(v('cogs', i)) ?? v('revenue', i))), 'Inventory ÷ COGS (or revenue) × 365'),
        row('payableDays', 'Payable days', 'days', 'up', each((i) => daysOf(v('payables', i), abs(v('cogs', i)) ?? v('revenue', i))), 'Payables ÷ COGS (or revenue) × 365'),
        row('ccc', 'Cash conversion cycle', 'days', 'down', each((i) => {
          const inv = daysOf(v('inventory', i), abs(v('cogs', i)) ?? v('revenue', i));
          const rec = daysOf(v('receivables', i), v('revenue', i));
          const pay = daysOf(v('payables', i), abs(v('cogs', i)) ?? v('revenue', i));
          return inv == null || rec == null || pay == null ? null : inv + rec - pay;
        }), 'Inventory days + receivable days − payable days'),
        row('wcDays', 'Net working-capital days', 'days', 'down', each((i) => {
          const inv = v('inventory', i), rec = v('receivables', i), pay = v('payables', i);
          if (inv == null && rec == null) return null;
          const nwc = (inv ?? 0) + (rec ?? 0) - (pay ?? 0);
          return daysOf(nwc, v('revenue', i));
        }), '(Inventory + receivables − payables) ÷ revenue × 365'),
        row('assetTurn', 'Asset turnover', 'x', 'up', each((i) => div(v('revenue', i), v('totalAssets', i))), 'Revenue ÷ total assets'),
        row('fixedAssetTurn', 'Fixed-asset turnover', 'x', 'up', each((i) => div(v('revenue', i), v('netFixedAssets', i))), 'Revenue ÷ net fixed assets'),
      ],
    },
    {
      key: 'cash', label: 'Cash flow quality',
      rows: [
        row('ocfEbitda', 'OCF / EBITDA', '%', 'up', each((i) => pctOf(v('ocf', i), v('ebitda', i))), 'Operating cash flow ÷ EBITDA — how much EBITDA turns into cash'),
        row('ocfPat', 'OCF / PAT', '%', 'up', each((i) => pctOf(v('ocf', i), v('pat', i))), 'Operating cash flow ÷ PAT — earnings quality'),
        row('fcf', 'Free cash flow', '₹Cr', 'up', each((i) => v('fcf', i)), 'OCF − capex'),
        row('fcfMargin', 'FCF margin', '%', 'up', each((i) => pctOf(v('fcf', i), v('revenue', i))), 'Free cash flow ÷ revenue'),
        row('capexRev', 'Capex / revenue', '%', null, each((i) => pctOf(abs(v('capex', i)), v('revenue', i))), 'Capex ÷ revenue — reinvestment intensity'),
      ],
    },
  ];

  // Drop rows with no data at all, then groups left empty — a ratio card of dashes is noise.
  const kept = groups
    .map((g) => ({ ...g, rows: g.rows.filter((r) => r.values.some((x) => x != null)) }))
    .filter((g) => g.rows.length);

  return { years, groups: kept, summary: ratioSummary(fs, s) };
}

const sum2 = (a: number | null, b: number | null) => (a == null && b == null ? null : (a ?? 0) + (b ?? 0));

/** CAGRs across the actual-year span (projection columns excluded). */
function ratioSummary(fs: FinancialStatements, s: Record<string, Series>): RatioSummary[] {
  const years = fs.years ?? [];
  const actuals = years.map((y, i) => ({ y, i })).filter(({ y }) => !isProjectedYear(y));
  const out: RatioSummary[] = [];
  const first = actuals[0], last = actuals[actuals.length - 1];
  if (!first || !last || first.i === last.i) return out;
  const yrs = fyNum(last.y) - fyNum(first.y);
  const span = `${first.y} → ${last.y}`;
  for (const [key, label] of [['revenueCagr', 'Revenue CAGR'], ['ebitdaCagr', 'EBITDA CAGR'], ['patCagr', 'PAT CAGR']] as const) {
    const k = key.replace('Cagr', '');
    const value = cagr(s[k]?.[first.i] ?? null, s[k]?.[last.i] ?? null, yrs);
    if (value != null) out.push({ key, label, unit: '%', value, span });
  }
  return out;
}

/* ───────────────────────── fallback: statements from the deck ───────────────────────── */

/**
 * Build a thin P&L from the deck-level figures a deal already carries (`pnl` rows + the
 * rev/pat/ebitda headline fields), so the Financials tab reads as a statement before the
 * data-room file arrives. Balance sheet and cash flow come back empty — a deck never states
 * them, and inventing them would be worse than an honest "not provided yet".
 *
 * The legacy `pnl` shape uses 0 for "not stated", so zeros are treated as missing here.
 */
export function statementsFromDeal(d: Pick<Deal, 'pnl' | 'rev25' | 'rev26' | 'pat25' | 'pat26' | 'ebitda26'>): FinancialStatements {
  const rows = (d.pnl ?? []).filter((r) => r && typeof r.y === 'string' && r.y.trim());
  const head: Record<string, { rev?: number; eb?: number; pat?: number }> = {
    FY25: { rev: d.rev25 || undefined, pat: d.pat25 || undefined },
    FY26: { rev: d.rev26 || undefined, pat: d.pat26 || undefined, eb: d.ebitda26 || undefined },
  };
  const years = [...new Set([...rows.map((r) => r.y.trim()), ...Object.entries(head).filter(([, h]) => h.rev != null || h.pat != null || h.eb != null).map(([y]) => y)])]
    .sort((a, b) => fyNum(a) - fyNum(b));

  const pick = (y: string, f: 'rev' | 'eb' | 'pat'): number | null => {
    const row = rows.find((r) => r.y.trim() === y);
    const fromRow = row ? Number(row[f]) : NaN;
    if (Number.isFinite(fromRow) && fromRow !== 0) return fromRow;
    const h = head[y]?.[f];
    return h != null && Number.isFinite(h) && h !== 0 ? h : null;
  };
  const line = (label: string, key: FinLineKey, f: 'rev' | 'eb' | 'pat', bold = false): FinLine =>
    ({ label, key, values: years.map((y) => pick(y, f)), bold });

  const lines = [line('Revenue', 'revenue', 'rev', true), line('EBITDA', 'ebitda', 'eb', true), line('PAT', 'pat', 'pat', true)]
    .filter((l) => l.values.some((x) => x != null));

  return {
    years,
    unit: '₹ Cr',
    pnl: { lines, note: 'Headline figures from the deck — not a full profit & loss statement.' },
    balanceSheet: EMPTY_STATEMENT,
    cashFlow: EMPTY_STATEMENT,
    source: 'deck',
  };
}

/**
 * The statements to render for a deal: the extracted data-room set when there is one, otherwise
 * the deck-level fallback. `derived` tells the UI which it got, so it can nudge for the upload.
 */
export function dealStatements(d: Pick<Deal, 'financials' | 'pnl' | 'rev25' | 'rev26' | 'pat25' | 'pat26' | 'ebitda26'>): { fs: FinancialStatements; derived: boolean } {
  if (finHasContent(d.financials)) return { fs: d.financials as FinancialStatements, derived: false };
  return { fs: statementsFromDeal(d), derived: true };
}

/* ───────────────────── keeping the deck-level fields in step ───────────────────── */

/**
 * Headline deal fields implied by the statements — so the Krit Score, valuation and credit
 * engines (which read rev25/pat26/de/ocfPat/wcDays) move with the data room instead of lagging
 * behind it. Only fields the statements actually support are returned; the caller patches
 * exactly those. `pnl` is rebuilt as the per-year revenue/EBITDA/PAT summary.
 */
export function headlineFromStatements(fs: FinancialStatements): Partial<Deal> {
  const out: Partial<Deal> = {};
  if (!finHasContent(fs)) return out;
  const years = fs.years ?? [];
  const s = finSeries(fs);
  const at = (k: string, i: number) => s[k]?.[i] ?? null;
  const idxOf = (fy: number) => years.findIndex((y) => fyNum(y) === fy && !isProjectedYear(y));

  const i25 = idxOf(25);
  if (i25 >= 0) {
    const rev = at('revenue', i25), pat = at('pat', i25);
    if (rev != null) out.rev25 = rev;
    if (pat != null) out.pat25 = pat;
  }
  const i26 = idxOf(26);
  if (i26 >= 0) {
    const rev = at('revenue', i26), pat = at('pat', i26), eb = at('ebitda', i26);
    if (rev != null) out.rev26 = rev;
    if (pat != null) out.pat26 = pat;
    if (eb != null) out.ebitda26 = eb;
  }

  // Yearly summary table (max 5 most recent columns, oldest → newest).
  const pnlRows = years
    .map((y, i) => ({ y, rev: at('revenue', i), eb: at('ebitda', i), pat: at('pat', i) }))
    .filter((r) => r.rev != null || r.eb != null || r.pat != null)
    .slice(-5)
    .map((r) => ({ y: r.y, rev: r.rev ?? 0, eb: r.eb ?? 0, pat: r.pat ?? 0 }));
  if (pnlRows.length) out.pnl = pnlRows;

  // Quality metrics for the score/credit engines, taken from the newest actual year.
  const i = latestActualIdx(years);
  if (i >= 0) {
    const debt = at('totalDebt', i), cash = at('cash', i), nw = at('netWorth', i);
    if (debt != null && nw != null && Math.abs(nw) > 1e-9) out.de = (debt - (cash ?? 0)) / nw;
    const ocf = at('ocf', i), pat = at('pat', i);
    if (ocf != null && pat != null && Math.abs(pat) > 1e-9) out.ocfPat = (ocf / pat) * 100;
    const inv = at('inventory', i), rec = at('receivables', i), pay = at('payables', i), rev = at('revenue', i);
    if (rev != null && Math.abs(rev) > 1e-9 && (inv != null || rec != null)) {
      out.wcDays = (((inv ?? 0) + (rec ?? 0) - (pay ?? 0)) / rev) * 365;
    }
  }
  return out;
}

/* ───────────────────────────── display formatting ───────────────────────────── */

/** A statement cell: ₹ Cr with one decimal under 1000, thousands separated, "—" when absent. */
export function fmtFin(v: number | null | undefined): string {
  const n = fin(v);
  if (n == null) return '—';
  const d = Math.abs(n) >= 1000 ? 0 : Math.abs(n) >= 100 ? 1 : 2;
  const s = Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
  return n < 0 ? `(${s})` : s;
}

/** A ratio cell, formatted for its unit. */
export function fmtRatio(v: number | null | undefined, unit: RatioRow['unit']): string {
  const n = fin(v);
  if (n == null) return '—';
  if (unit === '%') return `${n.toFixed(1)}%`;
  if (unit === 'x') return `${n.toFixed(2)}x`;
  if (unit === 'days') return `${Math.round(n)}d`;
  return fmtFin(n);
}

/** Common-size view: a line as % of that year's revenue (P&L only). */
export function commonSize(line: FinLine, revenue: Series): (number | null)[] {
  return line.values.map((x, i) => pctOf(fin(x), revenue[i] ?? null));
}

/**
 * Is this statement line a MONEY line? Per-share figures, ratios, %, day counts and headcounts
 * are not — they must never be unit-scaled on extraction, nor shown as a % of revenue in the
 * common-size view. Every alternative is word-bounded: an unbounded /ratio/ also matches
 * "ope-RATIO-ns", which would silently leave revenue unconverted.
 */
export const isMoneyLine = (label: string): boolean => !NON_MONEY.test(String(label ?? ''));

const NON_MONEY = /per share|\beps\b|\bdps\b|book value per|\bratios?\b|\bdays\b|\btimes\b|%|percent|\bnumber of\b|\bnos?\.?\s+of\b|headcount|\bemployees\b/i;

/**
 * The statements (and optionally the computed ratios) as markdown tables — one rendering shared by
 * the IC memo export and the AI assistant's deal context, so both read exactly what the tab shows.
 * Truncated to `maxChars` with an explicit marker rather than silently cut mid-table.
 */
export function statementsToMarkdown(fs: FinancialStatements, opts: { ratios?: boolean; maxChars?: number } = {}): string {
  if (!finHasContent(fs)) return '';
  const head = `| Line item | ${fs.years.join(' | ')} |\n|---|${fs.years.map(() => '---:').join('|')}|`;
  const table = (st: FinStatement): string =>
    [head, ...st.lines.map((l) => `| ${'  '.repeat(l.indent ?? 0)}${l.bold ? `**${l.label}**` : l.label} | ${l.values.map((v) => fmtFin(v)).join(' | ')} |`)].join('\n');

  const out: string[] = [`Financial statements — ₹ Cr${fs.source ? `, from ${fs.source}` : ''}${fs.notes?.length ? ` (${fs.notes.join('; ')})` : ''}`];
  for (const [title, st] of [['Profit & loss', fs.pnl], ['Balance sheet', fs.balanceSheet], ['Cash flow', fs.cashFlow]] as const) {
    if (statementHasContent(st)) out.push(`### ${title}\n${table(st)}${st.note ? `\n_${st.note}_` : ''}`);
  }
  if (opts.ratios) {
    const r = computeRatios(fs);
    if (r.groups.length) {
      const rows = r.groups.flatMap((g) => [
        `| **${g.label}** | ${g.rows[0] ? fs.years.map(() => '').join(' | ') : ''} |`,
        ...g.rows.map((x) => `| ${x.label} | ${x.values.map((v) => fmtRatio(v, x.unit)).join(' | ')} |`),
      ]);
      const cagrs = r.summary.map((s) => `${s.label} ${fmtRatio(s.value, s.unit)} (${s.span})`).join(' · ');
      out.push(`### Ratio analysis (computed from the statements above)\n${[head, ...rows].join('\n')}${cagrs ? `\n\n${cagrs}` : ''}`);
    }
  }
  const md = out.join('\n\n');
  const cap = opts.maxChars ?? 0;
  return cap > 0 && md.length > cap ? `${md.slice(0, cap)}\n… (truncated)` : md;
}
